/**
 * Renders a single shipment card for the Shipments tab.
 * Shows contact info, shipping address, shipping method with optional tracking link,
 * estimated delivery date, and an expandable accordion of OrderItemCards per shipment.
 */
import React from "react";
import { Accordion, Anchor, Box, Divider, createStyles } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { Flex } from "@rpi/openapi-core";
import { GraphQL } from "@rpi/openapi-api";
import { Body, Label } from "../../../../components/Typography";
import { OrderItemCard, type OrderItemFromQuery } from "../../../../components/OrderItemCard";
import {
  type Shipment,
  type Address,
  formatShippingMethodDisplay,
  formatEstimatedDeliveryDate,
  formatContactName,
  formatContactEmail,
  formatContactPhone,
  formatShippingAddressLines,
  buildTrackingUrl,
} from "./shipmentHelpers";

const useStyles = createStyles({
  card: {
    border: `1px solid ${tokens.color.semantic.bg.surfaceSunken}`,
    borderRadius: tokens.radius.lg,
    padding: tokens.spacing[4],
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[5],
  },
  fieldRow: {
    gap: tokens.spacing[6],
    padding: `0 ${tokens.spacing[4]}`,
  },
  fieldColumn: {
    gap: tokens.spacing[1],
    flex: "1 1 0",
    minWidth: 0,
  },
  accordionItem: {
    border: `1px solid ${tokens.color.semantic.bg.surfaceSunken}`,
    borderRadius: tokens.radius.sm,
  },
  accordionControl: {
    padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
  },
  itemDivider: {
    borderColor: tokens.color.semantic.bg.surfaceSunken,
  },
  accordionContent: {
    padding: `0 ${tokens.spacing[4]} ${tokens.spacing[3]}`,
  },
  itemsContainer: {
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[6],
  },
  trackingLink: {
    "font-weight": tokens.font.weight.semibold,
    textDecoration: "underline",
    color: "inherit",
  },
});

interface ShipmentCardProps {
  shipment: Shipment;
  currency: GraphQL.SupportedCurrency;
  isSuperAdmin: boolean;
}

export const ShipmentCard: React.FC<ShipmentCardProps> = ({ shipment, currency, isSuperAdmin }) => {
  const { classes } = useStyles();
  const address = shipment.destination?.address ?? undefined;
  const shipmentItems = (shipment.orderItems ?? []) as OrderItemFromQuery[];
  const showItemsAccordion = shipmentItems.length > 0;

  return (
    <Box className={classes.card}>
      <ContactAndAddressRow address={address} />
      <MethodAndDeliveryRow shipment={shipment} />
      {showItemsAccordion && (
        <ItemsAccordion orderItems={shipmentItems} currency={currency} isSuperAdmin={isSuperAdmin} />
      )}
    </Box>
  );
};

const ContactAndAddressRow: React.FC<{ address: Address | undefined }> = ({ address }) => {
  const { classes } = useStyles();

  if (!address) {
    return (
      <Flex.Row className={classes.fieldRow}>
        <FieldGroup label="Contact" lines={["-"]} />
        <FieldGroup label="Shipping Address" lines={["-"]} />
      </Flex.Row>
    );
  }

  return (
    <Flex.Row className={classes.fieldRow}>
      <FieldGroup
        label="Contact"
        lines={[formatContactName(address), formatContactEmail(address), formatContactPhone(address)]}
      />
      <FieldGroup label="Shipping Address" lines={formatShippingAddressLines(address)} />
    </Flex.Row>
  );
};

const MethodAndDeliveryRow: React.FC<{ shipment: Shipment }> = ({ shipment }) => {
  const { classes } = useStyles();
  const { method, trackingNumber } = formatShippingMethodDisplay(shipment);
  const trackingUrl = buildTrackingUrl(shipment);

  return (
    <Flex.Row className={classes.fieldRow}>
      <Flex.Column className={classes.fieldColumn}>
        <Label>Shipping Method</Label>
        <Body>
          {trackingNumber ? (
            <ShippingMethodWithTracking method={method} trackingNumber={trackingNumber} trackingUrl={trackingUrl} />
          ) : (
            method
          )}
        </Body>
      </Flex.Column>
      <Flex.Column className={classes.fieldColumn}>
        <Label>Estimated Delivery Date</Label>
        <Body>{formatEstimatedDeliveryDate(shipment)}</Body>
      </Flex.Column>
    </Flex.Row>
  );
};

interface ShippingMethodWithTrackingProps {
  method: string;
  trackingNumber: string;
  trackingUrl: string | null;
}

const ShippingMethodWithTracking: React.FC<ShippingMethodWithTrackingProps> = ({
  method,
  trackingNumber,
  trackingUrl,
}) => {
  const { classes } = useStyles();

  return (
    <>
      {method} - (
      {trackingUrl ? (
        <Anchor href={trackingUrl} target="_blank" rel="noopener noreferrer" className={classes.trackingLink}>
          {trackingNumber}
        </Anchor>
      ) : (
        <span className={classes.trackingLink}>{trackingNumber}</span>
      )}
      )
    </>
  );
};

const FieldGroup: React.FC<{ label: string; lines: string[] }> = ({ label, lines }) => {
  const { classes } = useStyles();
  return (
    <Flex.Column className={classes.fieldColumn}>
      <Label>{label}</Label>
      {lines.map((line, index) => (
        <Body key={index}>{line}</Body>
      ))}
    </Flex.Column>
  );
};

interface ItemsAccordionProps {
  orderItems: OrderItemFromQuery[];
  currency: GraphQL.SupportedCurrency;
  isSuperAdmin: boolean;
}

const ItemsAccordion: React.FC<ItemsAccordionProps> = ({ orderItems, currency, isSuperAdmin }) => {
  const { classes } = useStyles();
  const itemCountLabel = `Items (${orderItems.length})`;

  return (
    <Accordion
      chevronPosition="right"
      variant="default"
      classNames={{
        item: classes.accordionItem,
        control: classes.accordionControl,
        content: classes.accordionContent,
      }}
    >
      <Accordion.Item value="items">
        <Accordion.Control>
          <Label>{itemCountLabel}</Label>
        </Accordion.Control>
        <Accordion.Panel>
          <Box className={classes.itemsContainer}>
            {orderItems.map((item, index) => (
              <React.Fragment key={item.id}>
                {index > 0 && <Divider className={classes.itemDivider} />}
                <OrderItemCard orderItem={item} currency={currency} isSuperAdmin={isSuperAdmin} />
              </React.Fragment>
            ))}
          </Box>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};
